import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FiMenu, FiBook, FiCalendar, FiUsers, FiMail, FiUserCheck } from 'react-icons/fi';
import AdminSidebar from './AdminSidebar';
import { getDashboardStats } from '../../Redux/slices/adminSlice';

const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const dispatch = useDispatch();
  const { dashboard, loading, error } = useSelector(state => state.admin);
  const { isAuthenticated, user } = useSelector(state => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getDashboardStats());
    }
  }, [dispatch, isAuthenticated]);

  const stats = dashboard.statistics ? [
    { label: 'Total Sermons', value: dashboard.statistics.totalSermons || 0, icon: FiBook, color: 'bg-blue-500' },
    { label: 'Total Events', value: dashboard.statistics.totalEvents || 0, icon: FiCalendar, color: 'bg-green-500' },
    { label: 'Upcoming Events', value: dashboard.statistics.upcomingEvents || 0, icon: FiCalendar, color: 'bg-purple-500' },
    { label: 'Pending Ministry Requests', value: dashboard.statistics.pendingMinistryRequests || 0, icon: FiUsers, color: 'bg-yellow-500' },
    { label: 'Unread Messages', value: dashboard.statistics.unreadMessages || 0, icon: FiMail, color: 'bg-orange-500' },
    { label: 'Total Subscribers', value: dashboard.statistics.totalSubscribers || 0, icon: FiUserCheck, color: 'bg-teal-500' },
  ] : [];

  const recentSermons = dashboard.recentSermons || [];

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} currentPage="dashboard" />

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white shadow-sm">
          <div className="flex items-center justify-between p-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden">
              <FiMenu size={24} />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">Dashboard</h2>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-800">
                  {user ? `${user.firstName} ${user.lastName}` : 'Admin User'}
                </p>
                <p className="text-xs text-gray-500">{user?.email || 'admin@houseofpraise.com'}</p>
              </div>
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                {user ? user.firstName.charAt(0).toUpperCase() : 'A'}
              </div>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="p-6">
          {!isAuthenticated ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8 text-center">
              <p className="text-yellow-800 text-lg mb-4">Please log in to view dashboard data</p>
              <Link to="/admin/login" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg inline-block">
                Go to Login
              </Link>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
              <p className="text-red-800 text-lg mb-4">Error loading dashboard: {error}</p>
              <button 
                onClick={() => dispatch(getDashboardStats())}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg"
              >
                Retry
              </button>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {stats.map((stat, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm mb-1">{stat.label}</p>
                        <p className="text-3xl font-bold text-gray-800">{stat.value}</p>
                      </div>
                      <div className={`${stat.color} p-4 rounded-lg`}>
                        <stat.icon className="text-white" size={24} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl shadow-md p-6 mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Link to="/admin/sermons/add" className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition">
                    <FiBook size={20} />
                    <span>Add Sermon</span>
                  </Link>
                  <Link to="/admin/events/add" className="flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-lg transition">
                    <FiCalendar size={20} />
                    <span>Add Event</span>
                  </Link>
                  <Link to="/admin/ministry-requests" className="flex items-center justify-center space-x-2 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-4 px-6 rounded-lg transition relative">
                    <FiUsers size={20} />
                    <span>Ministry Requests</span>
                    {dashboard.statistics?.pendingMinistryRequests > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                        {dashboard.statistics.pendingMinistryRequests}
                      </span>
                    )}
                  </Link>
                  <Link to="/admin/messages" className="flex items-center justify-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold py-4 px-6 rounded-lg transition">
                    <FiMail size={20} />
                    <span>View Messages</span>
                  </Link>
                </div>
              </div>

              {/* Recent Sermons */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-800">Recent Sermons</h3>
                  <Link to="/admin/sermons" className="text-blue-600 hover:text-blue-700 font-medium">
                    View All
                  </Link>
                </div>
                {recentSermons.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-gray-600 font-semibold">Title</th>
                          <th className="text-left py-3 px-4 text-gray-600 font-semibold">Preacher</th>
                          <th className="text-left py-3 px-4 text-gray-600 font-semibold">Date</th>
                          <th className="text-left py-3 px-4 text-gray-600 font-semibold">Views</th>
                          <th className="text-left py-3 px-4 text-gray-600 font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentSermons.map((sermon) => (
                          <tr key={sermon._id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-gray-800">{sermon.title}</td>
                            <td className="py-3 px-4 text-gray-600">{sermon.preacher}</td>
                            <td className="py-3 px-4 text-gray-600">{new Date(sermon.date).toLocaleDateString()}</td>
                            <td className="py-3 px-4 text-gray-600">{sermon.views || 0}</td>
                            <td className="py-3 px-4">
                              <Link to={`/admin/sermons/edit/${sermon._id}`} className="text-blue-600 hover:text-blue-700 font-medium">
                                Edit
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No sermons yet</p>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
